import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ColumnMode, DatatableComponent } from '@swimlane/ngx-datatable';
import {
  DEFAULT_NGX_DATATABLE_CONFIG,
  NGX_DATATABLE_COMPACT_CELL_EXTRA,
  NGX_DATATABLE_DENSITY,
  NGX_DATATABLE_DENSITY_STORAGE_KEY,
  NGX_DATATABLE_SKELETON_ROWS,
  NGX_DATATABLE_WIDTHS_STORAGE_PREFIX,
} from 'src/app/core/helpers/ui/ngx-datatable.constant';
import {
  NgxDatatableConfig,
  NgxDatatableSort,
  NgxDatatableColumn,
} from 'src/app/core/interfaces/ui/ngx-datatable.interface';
import { MobileCardAction } from 'src/app/core/interfaces/ui/ngx-datatable-mobile.interface';
import { RESPONSIVE_CONFIG } from 'src/app/core/helpers/ui/toogle-panel.constant';

@Component({
  selector: 'shared-tables-ngx-datatable',
  templateUrl: './ngx-datatable.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class NgxDatatableComponent implements OnDestroy {
  public ngxConfig: NgxDatatableConfig = { ...DEFAULT_NGX_DATATABLE_CONFIG };
  public isCompact: boolean = this.readDensity() === NGX_DATATABLE_DENSITY.COMPACT;
  private baseColumns: Partial<NgxDatatableColumn>[] = [];
  private baseColumnMode: ColumnMode = DEFAULT_NGX_DATATABLE_CONFIG.columnMode;
  private compactWidths: Record<string, number> = {};
  private compactColumnMode: ColumnMode = ColumnMode.force;
  private measureTimer: ReturnType<typeof setTimeout> | null = null;
  private hasEmittedFirstHigherLimit: boolean = false;
  private _data: any[] = [];
  private lastRowCount: number = 0;
  private lastCount: number = 0;
  private skeletonCache: object[] = [];
  private _widthStorageKey: string = '';
  private savedWidths: Record<string, number> = {};
  public isMobileScreen: boolean = window.innerWidth < RESPONSIVE_CONFIG.MOBILE_BREAKPOINT;

  @Input({ required: true })
  set data(value: any[] | null) {
    if (!value || value.length === 0) {
      this._data = [];
      return;
    }

    this._data = [...value];
    this.lastRowCount = this._data.length;
    this.hasEmittedFirstHigherLimit = false;

    const columnsToSum = (this.ngxConfig.columns ?? [])
      .filter((col) => (col as any).shouldSum)
      .map((col) => col.prop as string);

    if (columnsToSum.length > 0 && this._data.length > 0) {
      const totalRow: any = { isTotalRow: true };

      const firstVisibleColumn = (this.ngxConfig.columns ?? []).find((c) => c.visible !== false);
      if (firstVisibleColumn?.prop) {
        totalRow[firstVisibleColumn.prop] = 'Total';
      } else {
        totalRow._showTotalInTemplate = true;
      }

      columnsToSum.forEach((prop) => {
        const sum = this.calculateSum(this._data, prop);
        totalRow[prop] = sum;
      });

      this.calculateUtilityGroup(
        totalRow,
        'shouldCalculateUtilityAmount',
        'shouldCalculateUtilityGain',
        'shouldCalculateUtility'
      );
      this.calculateUtilityGroup(
        totalRow,
        'shouldCalculateUtilityAmountBonus',
        'shouldCalculateUtilityGainBonus',
        'shouldCalculateUtilityBonus'
      );

      this._data.push(totalRow);
    }

    this.scheduleCompactMeasure();
  }

  get data(): any[] {
    return this._data;
  }

  @Input({ required: true })
  set config(value: Partial<NgxDatatableConfig> | null) {
    const defaultConfig = { ...DEFAULT_NGX_DATATABLE_CONFIG, ...value };

    defaultConfig.columns = (defaultConfig.columns ?? [])
      .filter((col: Partial<NgxDatatableColumn>) => col.visible !== false)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    if (defaultConfig.loadingIndicator && !defaultConfig.count) {
      defaultConfig.count = this.lastCount;
    } else if (!defaultConfig.loadingIndicator) {
      this.lastCount = defaultConfig.count ?? 0;
    }

    defaultConfig.columns = this.withSavedWidths(defaultConfig.columns);
    this.baseColumns = defaultConfig.columns;
    this.baseColumnMode = defaultConfig.columnMode;
    this.ngxConfig = this.isCompact ? this.withCompactLayout(defaultConfig) : defaultConfig;

    if (!defaultConfig.loadingIndicator) this.scheduleCompactMeasure();
  }

  @Input()
  set widthStorageKey(value: string) {
    this._widthStorageKey = value;
    this.savedWidths = this.readSavedWidths();
    this.baseColumns = this.withSavedWidths(this.baseColumns);
    const config = { ...this.ngxConfig, columns: this.baseColumns };
    this.ngxConfig = this.isCompact ? this.withCompactLayout(config) : config;
  }

  @Input() mobileActions: MobileCardAction[] = [];
  @Input() mobileCardTitleField: string = '';
  @Input() mobileCardSubtitleField: string = '';
  @Input() isLoading: boolean = false;
  @Input() disableMobileCards: boolean = false;

  get isLoadingRows(): boolean {
    return !!this.ngxConfig.loadingIndicator;
  }

  get skeletonRows(): object[] {
    const { limit, page, count } = this.ngxConfig;
    const pageRows = count > 0 ? Math.min(limit, count - (page - 1) * limit) : 0;
    const known = [this.lastRowCount, pageRows].filter((value) => value > 0);
    const rows = known.length ? Math.min(...known) : limit;
    const total = Math.min(
      Math.max(rows, NGX_DATATABLE_SKELETON_ROWS.MIN),
      NGX_DATATABLE_SKELETON_ROWS.MAX
    );

    if (this.skeletonCache.length !== total) {
      this.skeletonCache = Array.from({ length: total }, () => ({}));
    }
    return this.skeletonCache;
  }

  get isAutoLoading(): boolean {
    return this.isLoading || this.ngxConfig.loadingIndicator;
  }

  get shouldShowMobileCards(): boolean {
    return this.isMobileScreen && !this.disableMobileCards;
  }

  @Output() public onSelectRow = new EventEmitter<any>();
  @Output() public onChangeSort = new EventEmitter<NgxDatatableSort>();
  @Output() public onChangeLimit = new EventEmitter<number>();
  @Output() public onChangePage = new EventEmitter<number>();
  @Output() public onButtonAction = new EventEmitter<{
    action: string;
    row: any;
  }>();

  constructor(
    private _translate: TranslateService,
    private _host: ElementRef<HTMLElement>,
    private _cdr: ChangeDetectorRef
  ) {}

  public setCompact(compact: boolean): void {
    if (compact === this.isCompact) return;
    this.isCompact = compact;
    try {
      localStorage.setItem(
        NGX_DATATABLE_DENSITY_STORAGE_KEY,
        compact ? NGX_DATATABLE_DENSITY.COMPACT : NGX_DATATABLE_DENSITY.NORMAL
      );
    } catch {
      return;
    }

    const config = { ...this.ngxConfig, columns: this.baseColumns };
    this.ngxConfig = compact
      ? this.withCompactLayout(config)
      : { ...config, columnMode: this.baseColumnMode };
    this._cdr.markForCheck();
    this.scheduleCompactMeasure();
  }

  ngOnDestroy(): void {
    if (this.measureTimer) clearTimeout(this.measureTimer);
  }

  private readDensity(): string {
    try {
      return (
        localStorage.getItem(NGX_DATATABLE_DENSITY_STORAGE_KEY) ?? NGX_DATATABLE_DENSITY.NORMAL
      );
    } catch {
      return NGX_DATATABLE_DENSITY.NORMAL;
    }
  }

  private withCompactLayout(config: NgxDatatableConfig): NgxDatatableConfig {
    return {
      ...config,
      columnMode: this.compactColumnMode,
      columns: (config.columns ?? []).map((col) =>
        col.name && this.compactWidths[col.name]
          ? { ...col, width: this.compactWidths[col.name] }
          : col
      ),
    };
  }

  private scheduleCompactMeasure(): void {
    if (!this.isCompact) return;
    if (this.measureTimer) clearTimeout(this.measureTimer);
    this.measureTimer = setTimeout(() => this.measureCompactWidths());
  }

  private measureCompactWidths(): void {
    this.measureTimer = null;
    if (!this.isCompact || this.isLoadingRows) return;
    const table = this._host.nativeElement.querySelector<HTMLElement>('.ngx-datatable');
    if (!table) return;

    const widths: Record<string, number> = {};
    let total = 0;
    this.baseColumns.forEach((col, index) => {
      const nodes = table.querySelectorAll<HTMLElement>(
        `.ngx-col-${index} .datatable-body-cell-label, .ngx-col-${index} .datatable-header-cell-template-wrap`
      );
      let widest = 0;
      nodes.forEach((node) => (widest = Math.max(widest, node.scrollWidth)));
      const width = widest
        ? Math.ceil(widest) + NGX_DATATABLE_COMPACT_CELL_EXTRA
        : (col.width ?? 0);
      if (col.name && widest) widths[col.name] = width;
      total += width;
    });

    this.compactWidths = widths;
    this.compactColumnMode = total <= table.clientWidth ? ColumnMode.force : ColumnMode.standard;
    this.ngxConfig = this.withCompactLayout({ ...this.ngxConfig, columns: this.baseColumns });
    this._cdr.markForCheck();
  }

  public onColumnResize(event: { column: { name?: string }; newValue: number }): void {
    if (!this._widthStorageKey || this.isCompact) return;
    const column = (this.ngxConfig.columns ?? []).find(
      (col) => !!col.name && this._translate.instant(col.name) === event.column.name
    );
    if (!column?.name) return;

    this.savedWidths = { ...this.savedWidths, [column.name]: Math.round(event.newValue) };
    try {
      localStorage.setItem(
        NGX_DATATABLE_WIDTHS_STORAGE_PREFIX + this._widthStorageKey,
        JSON.stringify(this.savedWidths)
      );
    } catch {
      return;
    }
  }

  private readSavedWidths(): Record<string, number> {
    if (!this._widthStorageKey) return {};
    try {
      const raw = localStorage.getItem(NGX_DATATABLE_WIDTHS_STORAGE_PREFIX + this._widthStorageKey);
      const parsed: unknown = raw ? JSON.parse(raw) : {};
      return parsed && typeof parsed === 'object' ? (parsed as Record<string, number>) : {};
    } catch {
      return {};
    }
  }

  private withSavedWidths<T extends Partial<NgxDatatableColumn>>(columns?: T[]): T[] {
    return (columns ?? []).map((col) =>
      col.name && this.savedWidths[col.name] ? { ...col, width: this.savedWidths[col.name] } : col
    );
  }

  public emitOnSelectRow(selectedRow: any): void {
    if (!selectedRow) return;
    this.onSelectRow.emit(selectedRow);
  }

  public emitOnSort(event: any): void {
    const sort = event.sorts?.[0];
    if (!sort) return;
    const { prop, dir } = sort;
    this.onChangeSort.emit({ prop, dir });
  }

  public emitOnChangeLimit(event: Event): void {
    const limit = (event.target as HTMLSelectElement).value;
    const formattedLimit = parseInt(limit, 10);
    this.ngxConfig.limit = formattedLimit;
    const shouldEmitLimit = this.shouldEmitLimit(this.ngxConfig.count, this.ngxConfig.limit);
    if (shouldEmitLimit) {
      this.onChangeLimit.emit(this.ngxConfig.limit);
    }
  }

  public emitOnChangePage($event: { page: number }): void {
    if (!$event.page) return;
    this.ngxConfig.page = $event.page;
    this.onChangePage.emit(this.ngxConfig.page);
  }

  public emitOnChangeLimitFromMobile(limit: number): void {
    this.ngxConfig.limit = limit;
    const shouldEmitLimit = this.shouldEmitLimit(this.ngxConfig.count, this.ngxConfig.limit);
    if (shouldEmitLimit) {
      this.onChangeLimit.emit(this.ngxConfig.limit);
    }
  }

  public emitOnChangePageFromMobile(page: number): void {
    this.ngxConfig.page = page;
    this.onChangePage.emit(this.ngxConfig.page);
  }

  public toggleExpandRow(ngxDatatable: DatatableComponent, row: any): void {
    ngxDatatable.rowDetail.toggleExpandRow(row);
  }

  private shouldEmitLimit(count: number, limit: number): boolean {
    return (
      this.shouldEmitOnLimitExceeded(count, limit) || this.shouldEmitOnFirstLowerCount(count, limit)
    );
  }

  private shouldEmitOnLimitExceeded(count: number, limit: number): boolean {
    if (limit < count) {
      this.hasEmittedFirstHigherLimit = false;
      return true;
    }
    return false;
  }

  private shouldEmitOnFirstLowerCount(count: number, limit: number): boolean {
    if (count < limit && !this.hasEmittedFirstHigherLimit) {
      this.hasEmittedFirstHigherLimit = true;
      return true;
    }
    return false;
  }

  private calculateSum(data: any[], prop: string): string {
    const total = data.reduce((acc, row) => {
      const value = this.getNestedValue(row, prop);
      if (value == null || value === '') return acc;
      let num = typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) : value;
      if (isNaN(num)) return acc;
      return acc + num;
    }, 0);

    const isIntegerSum = this.ngxConfig.columns?.some(
      (col) => col.prop === prop && (col as any).shouldSumInteger
    );

    return isIntegerSum ? Math.round(total).toString() : total.toFixed(2);
  }

  private getNestedValue(obj: any, path: string): any {
    return path.split('.').reduce((acc, part) => acc && acc[part], obj);
  }

  private calculateUtilityGroup(
    totalRow: any,
    amountFlag: string,
    gainFlag: string,
    utilityFlag: string
  ): void {
    const columns = this.ngxConfig.columns ?? [];

    const amountCol = columns.find((col: any) => col[amountFlag]);
    const gainCol = columns.find((col: any) => col[gainFlag]);

    if (amountCol?.prop && gainCol?.prop) {
      const totalAmount = parseFloat(totalRow[amountCol.prop] || '0');
      const totalGain = parseFloat(totalRow[gainCol.prop] || '0');
      const profit = totalAmount - totalGain;
      const utilityValue =
        totalAmount > 0 ? ((profit / totalAmount) * 100).toFixed(2) + ' %' : '0.00 %';

      columns
        .filter((col: any) => col[utilityFlag])
        .forEach((col: any) => {
          if (col.prop) {
            totalRow[col.prop] = utilityValue;
          }
        });
    }
  }

  public getFormattedValue(value: any, col: Partial<NgxDatatableColumn>, row: any): string {
    if (col.valueFormatter) {
      return col.valueFormatter(value, row);
    }
    return typeof value === 'number' ? value.toFixed(2) : value;
  }

  public emitOnButtonAction(event: { action: string; row: any }): void {
    this.onButtonAction.emit(event);
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any): void {
    this.isMobileScreen = event.target.innerWidth < RESPONSIVE_CONFIG.MOBILE_BREAKPOINT;
    this.scheduleCompactMeasure();
  }

  get shouldApplyStickyHeader(): boolean {
    const result = this.data && this.data.length > 10;

    return result;
  }

  get datatableClass(): string {
    return this.shouldApplyStickyHeader ? 'material sticky-header' : 'material';
  }
}
